import React from 'react';
import { useCombobox } from 'downshift';
import styled, { useTheme, css } from 'styled-components/macro';
import { SPACINGS, useDebounce } from '../../utils';
import { IconLoader, IconChevron, IconX } from '../../icons';
import { sizesMap, textLineHeight } from '../../typography/utils';
import { Text } from '../../typography';
import { useTranslate } from '@federico.moretti/react-translate';
import chroma from 'chroma-js';
import { AsyncSelectProps } from './AsyncSelect';

const SelectContainer = styled.div`
  position: relative;
  width: ${(p) => p.theme.dimensions.inputWidth};

  .label {
    position: absolute;
    top: 0;
    padding: 0 ${SPACINGS[2]};
    padding-right: ${SPACINGS[2]};
    pointer-events: none;
    transform-origin: left;
    transition: transform 0.2s;

    &.up {
      transform: translateY(0) scale(0.8);
    }

    &.down {
      transform: translateY(${SPACINGS[5]}) scale(1);
    }
  }
`;

type SelectStyledType = {
  isOpen: boolean;
  disabled: boolean;
  isFocused: boolean;
  error: boolean;
  baseColor: string;
  mainColor: string;
  accentColor: string;
  inactiveColor: string;
  dangerColor: string;
  backgroundColor: string;
};

const SelectWrapperStyled = styled.div<SelectStyledType>`
  position: relative;
  width: 100%;
  background-color: ${(p) =>
    p.isFocused || p.isOpen
      ? chroma(p.accentColor).alpha(0.1).hex()
      : chroma(p.baseColor).alpha(0.1).hex()};
  border-bottom: 1px solid
    ${(p) =>
      p.error
        ? p.dangerColor
        : p.isOpen || p.isFocused
        ? p.accentColor
        : p.baseColor};
  border-radius: 4px 4px 0 0;
  padding: ${SPACINGS[6]} ${SPACINGS[2]} ${SPACINGS[1]};
  cursor: ${(p) => (p.disabled ? 'not-allowed' : 'auto')};

  & > * {
    cursor: ${(p) => (p.disabled ? 'not-allowed' : 'auto')};
  }

  &:hover {
    ${(p) =>
      !p.disabled &&
      css`
        background-color: ${chroma(p.accentColor).alpha(0.1).hex()};
        outline: none;
        border-bottom: 1px solid ${p.accentColor};
      `}
  }

  transition: border-color 0.15s ease-in-out, background-color 0.15s ease-in-out,
    box-shadow 0.15s ease-in-out;
`;

const InputStyled = styled.input<{ mainColor: string }>`
  width: 100%;
  height: 100%;
  background-color: transparent;
  border: none;
  outline: none;
  min-height: 24px;
  padding: 0;
  margin: 0;

  color: ${(p) => p.mainColor};
  font-family: ${(p) => p.theme.fonts.gtEesti};
  font-size: ${sizesMap['base'].size};
  line-height: ${textLineHeight};

  &:focus {
    outline: none;
  }
`;

const MenuWrapper = styled.ul`
  margin: 0;
  padding: 0;
  border-top: 0;
  outline: none;
  position: absolute;
  right: 0;
  left: 0;
  z-index: ${(p) => p.theme.zIndexes.overlay};
`;

const MenuStyled = styled.div`
  outline: none;
  padding: 0;
  margin: 0;
  margin-top: 1px;
  width: 100%;
  max-height: 10em;
  overflow: auto;
  background-color: ${(p) => p.theme.colors.white};
  box-shadow: ${(p) => p.theme.boxShadows.xs};
  border-radius: 0 0 4px 4px;
  z-index: ${(p) => p.theme.zIndexes.overlay};

  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
`;

type ItemStyledProps = {
  bold?: boolean;
  highlighted?: boolean;
  type?: string;
};
const ItemStyled = styled.li<ItemStyledProps>`
  list-style: none;
  padding: ${SPACINGS[2]};
  background-color: ${(p) =>
    p.highlighted ? p.theme.colors.primary80w : p.theme.colors.white};

  & span {
    color: ${(p) =>
      p.type === 'placeholder'
        ? p.theme.colors.secondary80w
        : p.theme.colors.black};
    font-weight: ${(p) => (p.bold ? 'bold' : 'unset')};
  }
`;

const CloseButton = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background-color: transparent;
  border: none;
  cursor: pointer;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 30px;
  top: 50%;
  transform: translateY(-50%);
  background-color: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
`;

export const Loader = styled.button`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background-color: transparent;
  border: none;
`;

function AsyncSelect<T>(props: AsyncSelectProps<T>) {
  const theme = useTheme();
  const {
    name,
    api,
    itemToString,
    onItemChange,
    onInputValueChange = () => {},
    initialInputValue = '',
    placeholder = 'Select...',
    style,
    id,
    clearInputOnChange,
    disabled,
    error,
    label,
    textColor,
    baseColor = theme.colors.secondary80w,
    mainColor = theme.colors.black,
    accentColor = theme.colors.primary,
    inactiveColor = theme.colors.secondary60w,
    dangerColor = theme.colors.danger,
    backgroundColor = theme.colors.white,
  } = props;

  const { t } = useTranslate();

  const [data, setData] = React.useState<T[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);

  const newId = id ?? name;
  const testid = props.testid ?? `${newId}-select`;

  const {
    isOpen,
    getToggleButtonProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    inputValue,
    setInputValue,
    selectedItem,
    getComboboxProps,
    getLabelProps,
  } = useCombobox({
    id: newId,
    stateReducer: (state, actionAndChanges) => {
      switch (actionAndChanges.type) {
        case useCombobox.stateChangeTypes.InputChange:
          return {
            ...actionAndChanges.changes,
            selectedItem:
              actionAndChanges.changes.inputValue === ''
                ? null
                : state.selectedItem,
          };
        default:
          return actionAndChanges.changes;
      }
    },
    onSelectedItemChange: (changes) => {
      setTimeout(() => onItemChange(changes.selectedItem));
      if (clearInputOnChange) {
        setInputValue('');
      }
    },
    onInputValueChange: (changes) => {
      if (changes.inputValue) {
        onInputValueChange(changes.inputValue);
      }
    },
    initialInputValue,
    items: data,
    itemToString,
  });

  const debouncedInputValue = useDebounce(inputValue, 500);

  React.useEffect(() => {
    let ignore = false;

    async function fetchApi(value: string) {
      if (api) {
        const res = await api(value);
        if (!ignore) {
          setData(res);
          setIsLoading(false);
        }
      }
    }

    if (api && debouncedInputValue !== '') {
      setIsLoading(true);
      fetchApi(debouncedInputValue);
    }

    return () => {
      ignore = true;
    };
  }, [api, debouncedInputValue]);

  const [isFocused, setIsFocused] = React.useState(false);

  function clearSelection() {
    onItemChange(null);
    setInputValue('');
  }

  const isLabelUp =
    isFocused || selectedItem || placeholder || inputValue?.length > 0;

  return (
    <SelectContainer style={style}>
      {label && (
        <Text
          className={`label ${isLabelUp ? 'up' : 'down'}`}
          margin="none"
          type="label"
          htmlFor={name}
          {...getLabelProps()}
        >
          {label}
        </Text>
      )}
      <SelectWrapperStyled
        isOpen={isOpen}
        disabled={Boolean(disabled)}
        isFocused={isFocused}
        error={Boolean(error)}
        baseColor={baseColor}
        mainColor={mainColor}
        accentColor={accentColor}
        inactiveColor={inactiveColor}
        dangerColor={dangerColor}
        backgroundColor={backgroundColor}
      >
        <div {...getComboboxProps()}>
          <InputStyled
            id={newId}
            data-testid={`${testid}-input`}
            placeholder={placeholder}
            error={error}
            isMenuOpen={isOpen}
            mainColor={textColor ?? mainColor}
            {...getInputProps({
              disabled,
              onFocus: () => setIsFocused(true),
              onBlur: () => setIsFocused(false),
            })}
          />
          {isLoading ? (
            <Loader>
              <IconLoader stroke={theme.colors.primary} />
            </Loader>
          ) : (
            <>
              {inputValue.length > 0 && !disabled && (
                <ClearButton
                  type="button"
                  onClick={clearSelection}
                  data-testid={`${testid}-clear-button`}
                >
                  <IconX scale={0.75} stroke={theme.colors.secondary60w} />
                </ClearButton>
              )}
              <CloseButton
                type="button"
                {...getToggleButtonProps({ disabled })}
                data-testid={`${testid}-menu-button`}
              >
                <IconChevron
                  stroke={disabled ? inactiveColor : accentColor}
                  direction={isOpen ? 'top' : 'bottom'}
                />
              </CloseButton>
            </>
          )}
        </div>
        <MenuWrapper {...getMenuProps()} data-testid={`${testid}-menu`}>
          {isOpen ? (
            <MenuStyled>
              {data.length > 0 ? (
                data.map((item, index) => (
                  <ItemStyled
                    data-testid={`${testid}-item-${index}`}
                    highlighted={highlightedIndex === index}
                    weight={
                      itemToString(item) === itemToString(selectedItem)
                        ? 'bold'
                        : 'normal'
                    }
                    key={index}
                    {...getItemProps({
                      item,
                      index,
                    })}
                  >
                    <Text margin="none">{itemToString(item)}</Text>
                  </ItemStyled>
                ))
              ) : (
                <ItemStyled type="placeholder">
                  <Text margin="none">{t('noOptions')}</Text>
                </ItemStyled>
              )}
            </MenuStyled>
          ) : null}
        </MenuWrapper>
      </SelectWrapperStyled>
      {typeof error === 'string' && (
        <Text
          id={`${newId}-error`}
          style={{ color: theme.colors.danger, marginTop: SPACINGS[2] }}
          margin="none"
        >
          {error}
        </Text>
      )}
    </SelectContainer>
  );
}

export default AsyncSelect;
